import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import usePageMeta from '@/hooks/usePageMeta';

const leistungen = [
  { value: 'Sondermaschinen & Anlagenbau', icon: 'ri-settings-3-line', label: 'Sondermaschinen & Anlagenbau' },
  { value: 'Steuerungstechnik, SPS & Automation', icon: 'ri-cpu-line', label: 'Steuerungstechnik, SPS & Automation' },
  { value: 'Roboter- & Handlingsysteme', icon: 'ri-robot-line', label: 'Roboter- & Handlingsysteme' },
  { value: 'Industrielle Bildverarbeitung', icon: 'ri-eye-line', label: 'Industrielle Bildverarbeitung' },
  { value: 'Mechanische Fertigung', icon: 'ri-tools-line', label: 'Mechanische Fertigung' },
  { value: 'Hard- & Softwareentwicklung', icon: 'ri-code-s-slash-line', label: 'Hard- & Softwareentwicklung' },
  { value: 'Lasertechnik', icon: 'ri-focus-3-line', label: 'Lasertechnik' },
  { value: 'Sonstiges', icon: 'ri-question-line', label: 'Sonstiges / Allgemeine Anfrage' },
];

const AnfragePage = () => {
  usePageMeta({
    title: 'Projekt anfragen – Kostenlose Beratung | gowiTEC Reichshof',
    description: 'Stellen Sie Ihre Projektanfrage an gowiTEC – kostenlos und unverbindlich. Sondermaschinenbau, SPS-Technik und Automatisierung aus Reichshof-Wehnrath, NRW.',
    keywords: 'Projektanfrage Maschinenbau, Automatisierung anfragen, SPS-Beratung kostenlos, gowiTEC Anfrage',
    canonical: 'https://gowitec.de/anfrage',
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [selectedLeistung, setSelectedLeistung] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => params.append(key, value.toString()));
    params.append('form_type', 'anfrage');

    setLoading(true);
    try {
      const res = await fetch('https://readdy.ai/api/form/d7fjrp95vm7vckkoe5t0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        navigate('/danke?typ=anfrage');
      } else {
        setError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt unter anfrage@gowitec.de.');
      }
    } catch {
      setError('Verbindungsfehler. Bitte kontaktieren Sie uns direkt unter anfrage@gowitec.de.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 bg-[#0f1f4a]">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-0.5 bg-[#DC2626]"></span>
              <span className="text-[#DC2626] text-xs font-semibold tracking-widest uppercase">Kontakt</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">Projekt anfragen</h1>
            <p className="text-blue-200 text-base md:text-lg max-w-xl leading-relaxed">
              Schildern Sie uns Ihre Anforderungen – wir melden uns innerhalb von 24 Stunden mit einer ersten Einschätzung.
            </p>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-20 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              {/* Left Info */}
              <div className="lg:col-span-2 flex flex-col gap-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">So arbeiten wir zusammen</h2>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Nach Ihrer Anfrage analysieren wir Ihre Anforderungen und melden uns schnellstmöglich mit einem ersten Lösungsvorschlag.
                  </p>
                </div>

                <ul className="flex flex-col gap-4">
                  {[
                    { icon: 'ri-mail-send-line', title: 'Anfrage senden', desc: 'Füllen Sie das Formular aus – dauert nur 2 Minuten.' },
                    { icon: 'ri-discuss-line', title: 'Erstgespräch', desc: 'Wir melden uns innerhalb von 24 Stunden für ein kostenloses Erstgespräch.' },
                    { icon: 'ri-file-list-3-line', title: 'Angebot erhalten', desc: 'Sie erhalten ein detailliertes, unverbindliches Angebot.' },
                    { icon: 'ri-rocket-line', title: 'Projekt starten', desc: 'Nach Ihrer Freigabe beginnen wir mit der Umsetzung.' },
                  ].map((step, i) => (
                    <li key={step.title} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#EFF6FF] rounded-xl shrink-0">
                        <i className={`${step.icon} text-[#1E3A8A] text-lg`}></i>
                      </div>
                      <div>
                        <div className="text-gray-900 font-semibold text-sm mb-0.5">
                          <span className="text-[#1E3A8A] mr-1">{i + 1}.</span>{step.title}
                        </div>
                        <div className="text-gray-500 text-xs leading-relaxed">{step.desc}</div>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="bg-white rounded-xl p-6 border border-gray-100 flex flex-col gap-4 mt-2">
                  <a href="tel:+491776512122" className="flex items-center gap-3 cursor-pointer group">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#EFF6FF] rounded-xl shrink-0">
                      <i className="ri-phone-line text-[#1E3A8A] text-lg"></i>
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs">Telefon</div>
                      <div className="text-gray-900 font-semibold text-sm group-hover:text-[#1E3A8A] transition-colors">+49 1776512122</div>
                    </div>
                  </a>
                  <a href="mailto:anfrage@gowitec.de" className="flex items-center gap-3 cursor-pointer group">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#EFF6FF] rounded-xl shrink-0">
                      <i className="ri-mail-line text-[#1E3A8A] text-lg"></i>
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs">E-Mail</div>
                      <div className="text-gray-900 font-semibold text-sm group-hover:text-[#1E3A8A] transition-colors">anfrage@gowitec.de</div>
                    </div>
                  </a>
                </div>

                <div className="flex items-center gap-2 text-gray-400 text-xs">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-check-line text-green-500"></i></span>
                  DSGVO-konform – Ihre Daten werden vertraulich behandelt.
                </div>
              </div>

              {/* Right Form */}
              <div className="lg:col-span-3">
                {submitted ? (
                  <div className="bg-white rounded-xl p-12 border border-gray-100 text-center flex flex-col items-center gap-4">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full">
                      <i className="ri-check-line text-green-500 text-3xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Anfrage gesendet!</h3>
                    <p className="text-gray-500 text-base max-w-sm">
                      Vielen Dank für Ihre Anfrage. Wir melden uns innerhalb von 24 Stunden bei Ihnen.
                    </p>
                  </div>
                ) : (
                  <form
                    id="projektanfrage-gowitec"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="bg-white rounded-xl p-8 md:p-10 border border-gray-100"
                  >
                    <h2 className="text-xl font-bold text-gray-900 mb-8">Ihre Projektanfrage</h2>

                    {/* Honeypot – für Bots unsichtbar */}
                    <div style={{ display: 'none' }} aria-hidden="true">
                      <input type="text" name="website" tabIndex={-1} autoComplete="off" />
                    </div>

                    {/* Leistungs-Auswahl als Kacheln */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Welchen Bereich möchten Sie anfragen? *
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                        {leistungen.map((l) => (
                          <button
                            key={l.value}
                            type="button"
                            onClick={() => setSelectedLeistung(l.value)}
                            className={`flex flex-col items-center gap-2 p-3 rounded-lg border text-center transition-all duration-150 cursor-pointer ${
                              selectedLeistung === l.value
                                ? 'border-[#1E3A8A] bg-[#EFF6FF] text-[#1E3A8A]'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-[#1E3A8A]/40 hover:text-[#1E3A8A]'
                            }`}
                          >
                            <span className="w-8 h-8 flex items-center justify-center">
                              <i className={`${l.icon} text-lg`}></i>
                            </span>
                            <span className="text-xs font-medium leading-tight">{l.label}</span>
                          </button>
                        ))}
                      </div>
                      <input type="hidden" name="leistung" value={selectedLeistung} />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="name">Name *</label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          placeholder="Max Mustermann"
                          className="w-full h-12 px-4 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1E3A8A] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="firma">Firma</label>
                        <input
                          id="firma"
                          name="firma"
                          type="text"
                          placeholder="Musterfirma GmbH"
                          className="w-full h-12 px-4 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1E3A8A] transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="email">E-Mail *</label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          placeholder="max@musterfirma.de"
                          className="w-full h-12 px-4 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1E3A8A] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="telefon">Telefon</label>
                        <input
                          id="telefon"
                          name="telefon"
                          type="tel"
                          placeholder="+49 (0) 123 456789"
                          className="w-full h-12 px-4 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1E3A8A] transition-colors"
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="nachricht">
                        Ihre Nachricht *
                        <span className="text-gray-400 font-normal ml-2 text-xs">{charCount}/500</span>
                      </label>
                      <textarea
                        id="nachricht"
                        name="nachricht"
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="Beschreiben Sie kurz Ihr Projekt oder Ihre Anforderungen..."
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1E3A8A] transition-colors resize-none"
                      />
                    </div>

                    <div className="flex items-start gap-3 mb-6">
                      <input
                        id="datenschutz"
                        name="datenschutz"
                        type="checkbox"
                        required
                        className="mt-0.5 cursor-pointer"
                      />
                      <label htmlFor="datenschutz" className="text-gray-500 text-xs leading-relaxed cursor-pointer">
                        Ich habe die <a href="/datenschutz" className="text-[#1E3A8A] hover:underline">Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu. *
                      </label>
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-600 flex items-center gap-2 mb-4">
                        <span className="w-4 h-4 flex items-center justify-center shrink-0">
                          <i className="ri-error-warning-line"></i>
                        </span>
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full h-14 bg-[#1E3A8A] hover:bg-[#1e40af] disabled:opacity-60 text-white font-semibold rounded-md transition-colors duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-loader-4-line animate-spin"></i>
                          </span>
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          Projekt anfragen
                          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line"></i></span>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AnfragePage;
